import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { ArrowLeft, ArrowRight, CheckCircle2, FileText, Palette, Sparkles, MapPin, Calendar, User, Home, Sun, Wallet, Building2, Ruler, Compass, Maximize2, Snowflake, Banknote, Mail, Phone, Trees, ArrowUpRight, Loader2, AlertCircle } from 'lucide-react';
import { CONFIG_STEPS } from '../mock/mock';

const STEP_ICONS = [FileText, Maximize2, Palette, Calendar, Wallet, User];
const OPTION_ICONS = {
  allando: Home, nyaralo: Sun, befektetes: Wallet, egyeb: Sparkles,
  igen: Home, nem: Compass, segit: Sparkles,
  '60-80': Maximize2, '80-120': Maximize2, '120-180': Maximize2, '180+': Maximize2,
  modern: Ruler, klasszikus: Building2, skandinav: Trees, mediterran: Sun,
  azonnal: Calendar, 'fel-ev': Calendar, ev: Calendar, kesobb: Calendar,
  '30-50': Banknote, '50-80': Banknote, '120+': Banknote,
};

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

// Build a readable answer summary from the configurator state
function buildSummary(answers) {
  const lines = [];
  CONFIG_STEPS.forEach((step, si) => {
    if (!step.questions) return;
    step.questions.forEach((q, qi) => {
      if (q.type === 'contact') return;
      const key = `${si}-${qi}`;
      const val = answers[key];
      if (!val) return;
      const opt = (q.options || []).find((o) => o.v === val);
      const label = opt ? opt.label : val;
      lines.push(`${step.title} – ${q.q}: ${label}`);
    });
  });
  return lines.join('\n');
}

// Get a specific answer label by step/question index
function getAnswerLabel(answers, si, qi) {
  const key = `${si}-${qi}`;
  const val = answers[key];
  if (!val) return '';
  const q = CONFIG_STEPS[si]?.questions?.[qi];
  const opt = (q?.options || []).find((o) => o.v === val);
  return opt ? opt.label : val;
}

export default function Configurator({ id = 'survey' }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: '', email: '', phone: '' });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const total = CONFIG_STEPS.length;
  const current = CONFIG_STEPS[step];
  const progress = ((step + 1) / total) * 100;

  const setAnswer = (q, v) => {
    setError('');
    setAnswers((prev) => ({ ...prev, [`${step}-${q}`]: v }));
  };

  const sendEmail = async () => {
    setError('');
    if (!contact.name.trim() || !contact.email.trim() || !contact.phone.trim()) {
      setError('Kérjük, töltse ki a név, e-mail és telefonszám mezőket.');
      return;
    }
    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      setError('Az e-mail küldés nincs megfelelően konfigurálva.');
      return;
    }
    try {
      setSending(true);
      const params = {
        // Contact fields
        from_name: contact.name,
        from_email: contact.email,
        name: contact.name,
        email: contact.email,
        phone: contact.phone,

        // Each survey answer as separate variable
        project_type: getAnswerLabel(answers, 0, 0),
        has_land: getAnswerLabel(answers, 0, 1),
        size: getAnswerLabel(answers, 1, 0),
        style: getAnswerLabel(answers, 2, 0),
        timing: getAnswerLabel(answers, 3, 0),
        budget: getAnswerLabel(answers, 4, 0),

        // Combined summary fields
        message: buildSummary(answers) || '(nincs megadott válasz)',
        summary: buildSummary(answers) || '(nincs megadott válasz)',

        // Meta
        submitted_at: new Date().toLocaleString('hu-HU'),
      };
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, params, { publicKey: PUBLIC_KEY });
      setDone(true);
    } catch (e) {
      console.error('EmailJS error', e);
      setError('Sajnáljuk, az üzenet küldése sikertelen. Kérjük próbálja újra később vagy írjon közvetlenül e-mailt.');
    } finally {
      setSending(false);
    }
  };

  const validateCurrentStep = () => {
    if (current.questions) {
      for (let qi = 0; qi < current.questions.length; qi++) {
        const q = current.questions[qi];
        if (q.type === 'contact') continue;
        const key = `${step}-${qi}`;
        if (!answers[key]) {
          return 'Kérjük, válassza ki a választ minden kérdésre a továbblépéshez.';
        }
      }
    }
    return '';
  };

  const next = () => {
    setError('');
    const stepError = validateCurrentStep();
    if (stepError) {
      setError(stepError);
      return;
    }
    if (step < total - 1) {
      setStep(step + 1);
    } else {
      sendEmail();
    }
  };
  const prev = () => { setError(''); step > 0 && setStep(step - 1); };

  if (done) {
    return (
      <section id={id} className="py-24 bg-[#f5f0e8]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="bg-white border border-[#e8e4dc] rounded-3xl p-12 shadow-sm">
            <CheckCircle2 className="w-16 h-16 text-[#2BA8C0] mx-auto mb-6" />
            <h3 className="font-serif-display text-4xl md:text-5xl text-[#1a1a1a] mb-4">Köszönjük!</h3>
            <p className="text-[#5a5a5a] text-base leading-relaxed max-w-lg mx-auto">
              Megkaptuk a válaszait. Kollégáink 24 órán belül személyre szabott ajánlattal keresik Önt a megadott elérhetőségen.
            </p>
            <button
              onClick={() => { setDone(false); setStep(0); setAnswers({}); setContact({ name:'', email:'', phone:'' }); }}
              className="mt-7 px-6 py-3 rounded-md border border-[#1a1a1a]/20 text-[#1a1a1a] text-sm hover:bg-[#1a1a1a] hover:text-white transition-colors"
            >Új konfiguráció</button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id={id} className="bg-white">
      <div className="grid lg:grid-cols-12 min-h-[760px]">
        {/* Left sidebar */}
        <aside className="lg:col-span-4 xl:col-span-4 relative bg-gradient-to-br from-[#0e2030] via-[#0d2336] to-[#0a1d2e] text-white p-10 lg:p-14 flex flex-col">
          <div className="absolute inset-0 opacity-30 pointer-events-none" style={{backgroundImage:'radial-gradient(circle at 80% 10%, rgba(43,168,192,0.25), transparent 60%)'}} />
          <div className="relative z-10">
            <h2 className="font-serif-display text-5xl md:text-6xl leading-[1.05] mb-5">
              Álomház<br /><span className="text-[#2BA8C0]">konfigurátor</span>
            </h2>
            <p className="text-white/65 text-[14px] leading-relaxed max-w-xs mb-12">
              6 egyszerű lépés, és kollégáink személyre szabott ajánlattal keresik Önt.
            </p>

            <div className="space-y-2.5">
              {CONFIG_STEPS.map((s, i) => {
                const Icon = STEP_ICONS[i];
                const active = i === step;
                const passed = i < step;
                return (
                  <button
                    key={i}
                    onClick={() => i <= step && setStep(i)}
                    className={`w-full flex items-center gap-4 p-3.5 rounded-xl text-left transition-all ${active ? 'bg-white/10 border border-white/15 ring-1 ring-[#2BA8C0]/40' : 'opacity-50 hover:opacity-80'} ${i > step ? 'cursor-default' : 'cursor-pointer'}`}
                  >
                    <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${active ? 'bg-[#2BA8C0]' : passed ? 'bg-[#2BA8C0]/30' : 'bg-white/10'}`}>
                      <Icon className="w-4 h-4 text-white" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className={`text-[14px] font-medium ${active ? 'text-white' : 'text-white/80'}`}>{s.title}</div>
                      <div className="text-[11px] uppercase tracking-wider text-white/50 mt-0.5 truncate">{s.subtitle}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="relative z-10 mt-auto pt-12">
            <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.2em] text-white/55 mb-3">
              <span>Haladás</span><span>{step + 1} / {total}</span>
            </div>
            <div className="h-1 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-[#2BA8C0] transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </aside>

        {/* Right content */}
        <div className="lg:col-span-8 xl:col-span-8 bg-white p-10 lg:p-16 flex flex-col">
          <div className="max-w-3xl w-full mx-auto flex-1 flex flex-col">
            <div className="mb-2">
              <span className="section-num text-[#2BA8C0] text-[80px] leading-none font-light">{current.num}</span>
            </div>
            <h3 className="text-[12px] uppercase tracking-[0.3em] text-[#9a9a9a] mb-2">{current.title}</h3>
            <h4 className="font-serif-display text-3xl md:text-4xl text-[#1a1a1a] mb-10">{current.subtitle}</h4>

            <div className="space-y-9 flex-1">
              {current.questions.map((q, qi) => (
                <div key={qi}>
                  {q.type === 'contact' ? (
                    <div className="space-y-4">
                      <p className="text-[#1a1a1a] font-medium text-[15px]">Elérhetőség</p>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="relative">
                          <User className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#9a9a9a]" />
                          <input placeholder="Teljes név" value={contact.name} onChange={(e)=>setContact({...contact,name:e.target.value})} className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-[#e0dcd2] bg-white text-[#1a1a1a] focus:outline-none focus:border-[#2BA8C0] transition-colors" />
                        </div>
                        <div className="relative">
                          <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#9a9a9a]" />
                          <input placeholder="E-mail cím" value={contact.email} onChange={(e)=>setContact({...contact,email:e.target.value})} className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-[#e0dcd2] bg-white text-[#1a1a1a] focus:outline-none focus:border-[#2BA8C0] transition-colors" />
                        </div>
                        <div className="relative md:col-span-2">
                          <Phone className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#9a9a9a]" />
                          <input placeholder="Telefonszám" value={contact.phone} onChange={(e)=>setContact({...contact,phone:e.target.value})} className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-[#e0dcd2] bg-white text-[#1a1a1a] focus:outline-none focus:border-[#2BA8C0] transition-colors" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-[#1a1a1a] font-medium mb-4 text-[15px]">{q.q}</p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {q.options.map((o) => {
                          const key = `${step}-${qi}`;
                          const active = answers[key] === o.v;
                          const Icon = OPTION_ICONS[o.v] || Home;
                          return (
                            <button
                              key={o.v}
                              onClick={() => setAnswer(qi, o.v)}
                              className={`text-left px-5 py-4 rounded-2xl border transition-all flex items-start gap-3 ${active ? 'border-[#2BA8C0] bg-[#2BA8C0]/5 ring-1 ring-[#2BA8C0]/30' : 'border-[#e8e4dc] hover:border-[#2BA8C0]/50 bg-white'}`}
                            >
                              <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${active ? 'bg-[#2BA8C0] text-white' : 'bg-[#f5f0e8] text-[#5a5a5a]'}`}>
                                <Icon className="w-4 h-4" />
                              </span>
                              <span className="flex-1">
                                <span className="block text-[#1a1a1a] font-medium text-[15px]">{o.label}</span>
                                {o.sub && <span className="block text-[12px] text-[#7a7a7a] mt-0.5">{o.sub}</span>}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {error && (
              <div className="mt-6 flex items-start gap-3 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-[13px]">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="mt-10 pt-6 border-t border-[#e8e4dc] flex items-center justify-between">
              <button onClick={prev} disabled={step===0 || sending} className="flex items-center gap-2 px-5 py-3 text-[#5a5a5a] hover:text-[#1a1a1a] disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-[14px]">
                <ArrowLeft className="w-4 h-4" /> Vissza
              </button>
              <button onClick={next} disabled={sending} className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#2BA8C0] text-white hover:bg-[#239ab1] disabled:opacity-70 disabled:cursor-not-allowed transition-colors text-[14px] font-medium shadow-md">
                {sending ? (
                  <>Küldés... <Loader2 className="w-4 h-4 animate-spin" /></>
                ) : step === total - 1 ? (
                  <>Benyújtás <ArrowUpRight className="w-4 h-4" /></>
                ) : (
                  <>Tovább <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
